---
id: drift-detection-analysis
title: Infrastructure Drift Analysis
category: iac
difficulty: advanced
tags:
  - drift
  - terraform
  - state
  - remediation
  - infrastructure
description: >
  Compares Terraform state against actual cloud resources, identifies
  drift, and proposes remediation strategies.
whenToUse: >
  When terraform plan shows unexpected changes, or as a scheduled
  drift detection check.
variables:
  - name: cloud
    desc: Cloud provider (AWS, Azure, GCP)
  - name: environment
    desc: Environment name
  - name: last_apply_date
    desc: Date of last successful terraform apply
  - name: plan_output
    desc: Output of terraform plan showing drift
  - name: recent_changes
    desc: Any known manual or automated changes in the period
tips:
  - Run terraform plan with -detailed-exitcode to programmatically detect drift.
  - Include recent change logs (CloudTrail, Activity Log) for better root cause analysis.
  - Schedule this prompt weekly in non-prod and daily in prod.
---

You are an expert SRE analyzing infrastructure drift.

## Environment
- Cloud: {{cloud}}
- Environment: {{environment}}
- Last known good apply: {{last_apply_date}}

## Terraform Plan Output
```
{{plan_output}}
```

## Recent Changes (last 7 days)
{{recent_changes}}

## Task
1. For each drifted resource:
   - Identify what changed (state vs actual)
   - Assess severity (critical/high/medium/low)
   - Hypothesize root cause (manual change, auto-scaling, external tool, etc.)
   - Recommend action (import, taint, apply, ignore)
2. Flag any security-relevant drift (IAM, network, encryption)
3. Suggest preventive measures

## Output Format (JSON)
{
  "drift_summary": {
    "total_resources": 0,
    "drifted": 0,
    "security_relevant": 0
  },
  "drifted_resources": [
    {
      "resource": "type.name",
      "attribute": "what changed",
      "expected": "value in state",
      "actual": "value in cloud",
      "severity": "critical | high | medium | low",
      "root_cause_hypothesis": "string",
      "recommended_action": "import | taint | apply | ignore",
      "action_command": "terraform command to run"
    }
  ],
  "preventive_measures": ["suggestion 1", "suggestion 2"]
}

<!-- EXAMPLE_OUTPUT -->

{
  "drift_summary": { "total_resources": 42, "drifted": 3, "security_relevant": 1 },
  "drifted_resources": [
    {
      "resource": "aws_security_group.api",
      "attribute": "ingress rules",
      "expected": "port 443 from 10.0.0.0/8",
      "actual": "port 443 from 0.0.0.0/0",
      "severity": "critical",
      "root_cause_hypothesis": "Manual console change to debug connectivity issue",
      "recommended_action": "apply",
      "action_command": "terraform apply -target=aws_security_group.api"
    }
  ],
  "preventive_measures": ["Enable AWS Config rules for security group changes", "Set up SCPs to prevent console modifications in prod"]
}
